import { useLayoutEffect, useRef, type ChangeEvent } from "react";

/**
 * The only text input in the application.
 *
 * Numeric and email fields use `type="text"` with an `inputMode`, never `type="number"`
 * or `type="email"`. Those types return `null` from `selectionStart` and throw
 * `InvalidStateError` on `setSelectionRange`, so an interface that re-renders on input
 * cannot restore the caret: it lands at position 0 and the text appears to type
 * backwards. `type="number"` additionally lets a scroll wheel change a value that feeds a
 * duration determination.
 *
 * This was a real defect, not a hypothetical — data dictionary §9 and the prohibited list
 * in design/tokens.json both name it.
 *
 * Where a caret position cannot be read for any reason, it goes to the end of the value
 * rather than defaulting to 0.
 */

export type FieldKind = "text" | "numeric" | "decimal" | "email" | "tel";

const INPUT_MODE: Record<FieldKind, React.HTMLAttributes<HTMLInputElement>["inputMode"]> = {
  text: "text",
  numeric: "numeric",
  decimal: "decimal",
  email: "email",
  tel: "tel",
};

export interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  kind?: FieldKind;
  hint?: string;
  error?: string;
  required?: boolean;
  autoComplete?: string;
  /** The prototype's example text. Never a value — a placeholder is not an answer. */
  placeholder?: string;
  /**
   * Renders `type="password"`. Permitted: `password` is one of the types that *does*
   * support the selection APIs, so the caret can still be restored. The prohibition is on
   * `number` and `email`, which do not.
   */
  secret?: boolean;
}

export function TextField({
  id,
  label,
  value,
  onChange,
  kind = "text",
  hint,
  error,
  required = false,
  autoComplete,
  placeholder,
  secret = false,
}: TextFieldProps) {
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const input = ref.current;
    if (!input || caret.current === null) return;
    const position = Math.min(caret.current, input.value.length);
    input.setSelectionRange(position, position);
    caret.current = null;
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // selectionStart is readable because the field is type="text". If it ever is not,
    // fall back to the end of the value — never to 0.
    caret.current = event.target.selectionStart ?? event.target.value.length;
    onChange(event.target.value);
  };

  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="field">
      <label className="f" htmlFor={id}>
        {label} {required ? <b>*</b> : null}
        {hint ? <i id={`${id}-hint`}> {hint}</i> : null}
      </label>
      <input
        ref={ref}
        id={id}
        // text, or password where the value must not be shown. Never number or email.
        type={secret ? "password" : "text"}
        inputMode={secret ? undefined : INPUT_MODE[kind]}
        className="t"
        value={value}
        onChange={handleChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {error ? (
        <p className="err" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
